import { autoUpdate, flip, offset as offsetFn, size, useFloating } from '@floating-ui/react-dom';
import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';

import { useUniqueID } from 'support/react/hooks';

import { AbstractDropdownContext } from './AbstractDropdownContext';
import {
  ABSTRACT_DROPDOWN_ITEM_ID_PREFIX,
  ABSTRACT_DROPDOWN_MENU_ID_PREFIX,
  ABSTRACT_DROPDOWN_TOGGLE_ID_PREFIX,
} from './constants';

export interface AbstractDropdownProps {
  children?: React.ReactNode;

  /** Whether the popup should try to have the same width as the toggle button. */
  matchToggleWidth?: boolean;

  /** The distance between the menu and the toggle button. */
  offset?: number;

  /** Whether the popup should collapse when one of its items is clicked. */
  shouldCollapseOnItemClick?: boolean;
}

export function AbstractDropdown({
  children,
  matchToggleWidth,
  offset = 10,
  shouldCollapseOnItemClick = true,
}: AbstractDropdownProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [menuElement, setMenuElement] = useState<HTMLElement | null>(null);
  const [toggleElement, setToggleElement] = useState<HTMLElement | null>(null);
  const [menuWidth, setMenuWidth] = useState<number | null>(null);

  const itemsID = useUniqueID(ABSTRACT_DROPDOWN_MENU_ID_PREFIX);
  const toggleID = useUniqueID(ABSTRACT_DROPDOWN_TOGGLE_ID_PREFIX);

  const middleware = useMemo(() => {
    const middleware = [offsetFn(offset), flip()];

    if (matchToggleWidth) {
      middleware.push(
        size({
          apply({ rects }) {
            setMenuWidth(rects.reference.width);
          },
        }),
      );
    }

    return middleware;
  }, [matchToggleWidth, offset]);

  const { floating, reference, strategy, update, x, y } = useFloating({
    middleware,
    placement: 'bottom-start',
    strategy: 'fixed',
  });

  useEffect(() => {
    floating(menuElement);
    reference(toggleElement);
  }, [floating, menuElement, reference, toggleElement]);

  useEffect(() => {
    if (!matchToggleWidth) {
      setMenuWidth(null);
    }
  }, [matchToggleWidth]);

  useEffect(() => {
    if (!isExpanded || !toggleElement || !menuElement) {
      return;
    }

    return autoUpdate(toggleElement, menuElement, update);
  }, [isExpanded, menuElement, toggleElement, update]);

  const dropdown = useMemo(
    () => ({
      focusFirstItem() {
        const items = queryAllItems(document.getElementById(itemsID));
        items[0]?.focus();
      },
      focusLastItem() {
        const items = queryAllItems(document.getElementById(itemsID));
        items[items.length - 1]?.focus();
      },
      focusNextItem(currentItem: HTMLElement) {
        const items = queryAllItems(document.getElementById(itemsID));
        const currentIndex = items.indexOf(currentItem);
        if (currentIndex !== -1) {
          if (currentIndex === items.length - 1) {
            items[0]?.focus();
          } else {
            items[currentIndex + 1]?.focus();
          }
        }
      },
      focusPreviousItem(currentItem: HTMLElement) {
        const items = queryAllItems(document.getElementById(itemsID));
        const currentIndex = items.indexOf(currentItem);
        if (currentIndex !== -1) {
          if (currentIndex === 0) {
            items[items.length - 1]?.focus();
          } else {
            items[currentIndex - 1]?.focus();
          }
        }
      },
      focusToggle() {
        document.getElementById(toggleID)?.focus();
      },
      isExpanded,
      itemsID,
      menuStyle: {
        left: x ?? undefined,
        position: strategy,
        top: y ?? undefined,
        width: menuWidth ?? undefined,
      },
      setIsExpanded,
      setMenuElement,
      setToggleElement,
      shouldCollapseOnItemClick,
      toggleID,
    }),
    [isExpanded, itemsID, menuWidth, shouldCollapseOnItemClick, strategy, toggleID, x, y],
  );

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (
        event.target instanceof Element &&
        !event.target.closest(`[id="${itemsID}"`) &&
        !event.target.closest(`[id="${toggleID}"`)
      ) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [itemsID, toggleID]);

  return <AbstractDropdownContext.Provider value={dropdown}>{children}</AbstractDropdownContext.Provider>;
}

function queryAllItems(itemsElement: HTMLElement | null): HTMLElement[] {
  if (!itemsElement) {
    return [];
  }

  return Array.from(itemsElement.querySelectorAll(`[id^="${ABSTRACT_DROPDOWN_ITEM_ID_PREFIX}"]`));
}
